// SuperAdmin — Servisfy Status & Updates authoring. Owner-only (backend require_superadmin_session is the
// security boundary). Publishing a post NEVER activates a feature. Curated, platform-safe content only.
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { statusAdmin, toLocalTime } from '../../lib/statusApi';

const TYPES = ['service_status', 'incident', 'maintenance', 'new_feature', 'release_note', 'announcement'];
const SEVERITIES = ['info', 'minor', 'major', 'critical'];
const PLANES = ['business_web', 'android_business', 'ios_business', 'backend', 'all'];
const LIFECYCLES = ['', 'draft', 'scheduled', 'published', 'resolved', 'archived'];

const EMPTY = { type: 'announcement', title: '', summary: '', details: '', severity: 'info',
  affected_planes: [], feature_key: '', release_reference: '', internal_note: '' };

export default function StatusUpdatesAdmin() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  // Distinct list-bootstrap state so a 401 is NEVER rendered as an authenticated empty feed.
  const [loadState, setLoadState] = useState('loading'); // loading | ready | unauthorized | error
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;   // duplicate-request control — no overlapping/looping fetches
    inFlight.current = true;
    setLoadState('loading'); setError('');
    try {
      const r = await statusAdmin.list(filter || undefined);
      setPosts(r?.items || []);
      setLoadState('ready');
    } catch (e) {
      // A 401 is an unauthorized/expired SuperAdmin session — a truthful state, NOT an empty feed.
      if (e && e.status === 401) { setLoadState('unauthorized'); }
      else { setLoadState('error'); setError('Failed to load posts.'); }
    } finally { inFlight.current = false; }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const unauthorized = loadState === 'unauthorized';

  const act = async (fn, ...args) => {
    setBusy(true); setError('');
    try { await fn(...args); await load(); }
    catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    await act(statusAdmin.create, { ...form, lifecycle: 'draft' });
    setForm(EMPTY);
  };

  const togglePlane = (p) => setForm((f) => ({
    ...f, affected_planes: f.affected_planes.includes(p)
      ? f.affected_planes.filter((x) => x !== p) : [...f.affected_planes, p],
  }));

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" style={{ overflowY: 'auto' }}>
      <h2 className="text-xl font-bold text-gray-900 mb-1">Status &amp; Updates Management</h2>
      <p className="text-sm text-gray-500 mb-3">
        Create, schedule, publish, update, resolve and archive platform notices. Publishing a post does not
        enable any feature.
      </p>
      <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4 space-y-1">
        <p>• Notices published here populate the <strong>Business Status &amp; Updates</strong> feed — the same authoritative feed consumed by <strong>Business Web, Android Business and iOS Business</strong>.</p>
        <p>• <strong>Drafts</strong> are never visible to Business users; <strong>scheduled</strong> notices appear only once their publish time is reached.</p>
        <p>• <strong>Resolved</strong> incidents move into <strong>History</strong>. <strong>Commercial</strong> notices are additive — they never replace incidents, maintenance, release notes or What&apos;s New.</p>
      </div>
      {error ? <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}

      {/* Create */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6 bg-white">
        <h3 className="font-semibold text-sm mb-3">New post (draft)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-sm">Type
            <select className="mt-1 w-full border rounded px-2 py-1" value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-sm">Severity
            <select className="mt-1 w-full border rounded px-2 py-1" value={form.severity}
                    onChange={(e) => setForm({ ...form, severity: e.target.value })}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm md:col-span-2">Title
            <input className="mt-1 w-full border rounded px-2 py-1" value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className="text-sm md:col-span-2">Summary
            <input className="mt-1 w-full border rounded px-2 py-1" value={form.summary}
                   onChange={(e) => setForm({ ...form, summary: e.target.value })} />
          </label>
          <label className="text-sm md:col-span-2">Details
            <textarea className="mt-1 w-full border rounded px-2 py-1" rows={3} value={form.details}
                      onChange={(e) => setForm({ ...form, details: e.target.value })} />
          </label>
          <label className="text-sm">Feature key (What&apos;s New link)
            <input className="mt-1 w-full border rounded px-2 py-1" value={form.feature_key}
                   onChange={(e) => setForm({ ...form, feature_key: e.target.value })} />
          </label>
          <label className="text-sm">Release reference
            <input className="mt-1 w-full border rounded px-2 py-1" value={form.release_reference}
                   onChange={(e) => setForm({ ...form, release_reference: e.target.value })} />
          </label>
          <div className="text-sm md:col-span-2">Affected planes
            <div className="mt-1 flex flex-wrap gap-2">
              {PLANES.map((p) => (
                <button key={p} type="button" onClick={() => togglePlane(p)}
                        className={`px-2 py-0.5 rounded text-xs border ${form.affected_planes.includes(p)
                          ? 'bg-purple-100 border-purple-300 text-purple-800' : 'border-gray-300 text-gray-600'}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button disabled={busy || unauthorized} onClick={create}
                className="mt-3 px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50">
          Create draft
        </button>
        {unauthorized ? <span className="ml-2 text-xs text-amber-700">Sign in again to author notices.</span> : null}
      </div>

      {/* List + actions */}
      <div className="flex items-center gap-2 mb-2">
        <label className="text-sm">Filter
          <select className="ml-2 border rounded px-2 py-1" value={filter} onChange={(e) => setFilter(e.target.value)}>
            {LIFECYCLES.map((l) => <option key={l} value={l}>{l || 'all'}</option>)}
          </select>
        </label>
        <button onClick={load} className="text-sm text-purple-600 hover:underline">Refresh</button>
      </div>

      <div className="space-y-3">
        {loadState === 'loading' ? <p className="text-sm text-gray-400">Loading…</p> : null}
        {unauthorized ? (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Your SuperAdmin session isn’t authorized (or has expired). Please sign in again to manage notices.
          </div>
        ) : null}
        {loadState === 'error' ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            Failed to load posts. <button onClick={load} className="underline">Retry</button>
          </div>
        ) : null}
        {loadState === 'ready' && posts.map((p) => (
          <div key={p.post_id} className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="flex items-center justify-between gap-2">
              <div>
                <span className="font-medium text-sm">{p.title}</span>
                <span className="ml-2 text-xs text-gray-400">{p.type} · {p.lifecycle} · {p.severity}</span>
              </div>
              <span className="text-xs text-gray-400">{toLocalTime(p.published_at || p.created_at)}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {p.lifecycle === 'draft' || p.lifecycle === 'scheduled'
                ? <button disabled={busy} onClick={() => act(statusAdmin.publish, p.post_id)} className="text-xs px-2 py-0.5 rounded bg-green-600 text-white">Publish</button>
                : <button disabled={busy} onClick={() => act(statusAdmin.unpublish, p.post_id)} className="text-xs px-2 py-0.5 rounded border">Unpublish</button>}
              {p.type === 'incident' ? (
                <>
                  <button disabled={busy} onClick={() => { const m = window.prompt('Incident update'); if (m) act(statusAdmin.incidentUpdate, p.post_id, m); }} className="text-xs px-2 py-0.5 rounded border">Post update</button>
                  <button disabled={busy} onClick={() => act(statusAdmin.resolve, p.post_id, 'Resolved')} className="text-xs px-2 py-0.5 rounded border">Resolve</button>
                </>
              ) : null}
              {p.type === 'maintenance' ? (
                <>
                  <button disabled={busy} onClick={() => act(statusAdmin.maintenanceStart, p.post_id)} className="text-xs px-2 py-0.5 rounded border">Start</button>
                  <button disabled={busy} onClick={() => act(statusAdmin.maintenanceComplete, p.post_id)} className="text-xs px-2 py-0.5 rounded border">Complete</button>
                </>
              ) : null}
              <button disabled={busy} onClick={async () => { try { const r = await statusAdmin.preview(p.post_id); setPreview(r.business_projection); } catch (e) { setError('preview failed'); } }} className="text-xs px-2 py-0.5 rounded border">Preview</button>
              <button disabled={busy} onClick={() => act(statusAdmin.archive, p.post_id)} className="text-xs px-2 py-0.5 rounded border text-red-600">Archive</button>
            </div>
          </div>
        ))}
        {loadState === 'ready' && posts.length === 0 ? <p className="text-sm text-gray-400">No posts.</p> : null}
      </div>

      {preview ? (
        <div className="mt-6 border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm">Business-facing preview</h3>
            <button onClick={() => setPreview(null)} className="text-xs text-gray-500">Close</button>
          </div>
          <div className="text-sm"><b>{preview.title}</b> — {preview.summary}</div>
          {preview.details ? <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{preview.details}</div> : null}
          <div className="text-xs text-gray-400 mt-2">by {preview.author_display}</div>
        </div>
      ) : null}
    </div>
  );
}
